import Seo from "@/components/Seo";
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, BadgeCheck, Crown, Rocket, ExternalLink, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/Shared";
import { useConfig } from "@/lib/useConfig";

const PLAN_META = {
  starter: {
    icon: Rocket, badge: "Recruiter",
    tagline: "For small teams hiring occasionally",
    features: ["Post unlimited standard job listings", "Recruiter badge on your listings", "Company profile page", "Listings live for 30 days", "Email support"],
  },
  pro: {
    icon: BadgeCheck, badge: "Top Recruiter", popular: true,
    tagline: "For growing teams hiring actively",
    features: ["Everything in Starter", "Top Recruiter badge on all listings", "Featured placement on the jobs board", "Listed in the Top Recruiters section", "Priority review & support"],
  },
  elite: {
    icon: Crown, badge: "Elite Recruiter",
    tagline: "For companies hiring at scale",
    features: ["Everything in Pro", "Elite Recruiter badge", "Premium homepage placement", "Top of the Top Recruiters section", "Dedicated account manager", "Social media mentions"],
  },
};

export default function Recruiters() {
  const [plans, setPlans] = useState([]);
  const { paymentsEnabled } = useConfig();

  useEffect(() => {
    api.get("/recruiters/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="recruiters-page">
      <Seo title="Recruiter Plans — Hire AI Talent | WorldAIInfo" description="Reach AI and machine-learning professionals. Post jobs and promote your roles on WorldAIInfo's global AI jobs board." canonicalPath="/recruiters" />
      <PageHeader overline="For Employers" title="Recruiter Plans"
        subtitle="Hire great AI talent. Post jobs to our growing community, get featured placement, and stand out with recruiter badges." />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((p, i) => {
            const meta = PLAN_META[p.key] || {};
            const Icon = meta.icon || Rocket;
            return (
              <motion.div key={p.key} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`rounded-2xl p-7 border flex flex-col relative ${meta.popular ? "border-[#00F0FF]/50 bg-[#00F0FF]/[0.05]" : "border-white/10 bg-[#0e1424]"}`}
                data-testid={`recruiter-plan-${p.key}`}>
                {meta.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 font-mono-tech text-[10px] uppercase tracking-wider bg-[#00F0FF] text-[#04121a] px-3 py-1 rounded-full">
                    Most popular
                  </span>
                )}
                <div className="w-11 h-11 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#00F0FF]" />
                </div>
                <h3 className="font-display font-bold text-xl">{p.label}</h3>
                <p className="text-sm text-[#94A3B8] mt-1 mb-4">{meta.tagline}</p>
                {paymentsEnabled ? (
                  <div className="flex items-end gap-1 mb-1">
                    <span className="font-display font-extrabold text-4xl text-[#00F0FF]">${p.amount}</span>
                    <span className="text-[#94A3B8] text-sm mb-1.5">/month</span>
                  </div>
                ) : (
                  <div className="flex items-end gap-2 mb-1">
                    <span className="font-display font-extrabold text-4xl text-[#00F0FF]">Free</span>
                    <span className="text-[#94A3B8] text-sm mb-1.5">while we grow</span>
                  </div>
                )}
                <span className="font-mono-tech text-[10px] uppercase self-start px-2 py-0.5 rounded bg-[#F59E0B]/15 text-[#FBBF24] border border-[#F59E0B]/30 mb-5">
                  {meta.badge} badge
                </span>
                <ul className="space-y-2.5 text-sm text-[#94A3B8] flex-1 mb-6">
                  {(meta.features || []).map((f, idx) => (
                    <li key={idx} className="flex gap-2"><Check className="w-4 h-4 text-[#22C55E] shrink-0 mt-0.5" /> {f}</li>
                  ))}
                </ul>
                {paymentsEnabled ? (
                  <a href={p.payment_link} target="_blank" rel="noreferrer" data-testid={`recruiter-subscribe-${p.key}`}
                    className={`w-full inline-flex items-center justify-center gap-1.5 font-semibold px-5 py-3 rounded-md transition text-sm ${meta.popular ? "bg-[#00F0FF] text-[#04121a] hover:brightness-110" : "border border-white/15 hover:border-[#00F0FF]/60"}`}>
                    Subscribe Now <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                ) : (
                  <Link to="/jobs" data-testid={`recruiter-subscribe-${p.key}`}
                    className={`w-full inline-flex items-center justify-center gap-1.5 font-semibold px-5 py-3 rounded-md transition text-sm ${meta.popular ? "bg-[#00F0FF] text-[#04121a] hover:brightness-110" : "border border-white/15 hover:border-[#00F0FF]/60"}`}>
                    Post a Job – Free <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </motion.div>
            );
          })}
        </div>
        <p className="text-center text-xs text-[#64748B] mt-8 max-w-2xl mx-auto">
          {paymentsEnabled
            ? "Secure recurring billing hosted by Stripe. Cancel anytime."
            : "Founding listings are free while we grow our community. Every listing is reviewed before going live to keep quality high."}
        </p>
      </div>
    </div>
  );
}
